
import { PropType, computed, defineComponent, nextTick, ref } from 'vue'
import CustomActivatorSelect from '../../inputs/CustomActivatorSelect.vue'
import SmileSelector from '../../ui/SmileSelector.vue'

// Переменные с предсказуемой длиной значения — считаются в счётчике по этой длине.
// Остальные длину не задают (hasDynamic), считаются как «+» / «от N SMS».
const SMS_FIXED_LENGTHS: Record<string, number> = {
    '{дата-записи}': 10,
    '{время-записи}': 5,
    '{дата-и-время}': 16,
}

function pluralRu(n: number, one: string, few: string, many: string): string {
    const m10 = n % 10
    const m100 = n % 100
    if (m10 === 1 && m100 !== 11) return one
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
    return many
}

export default defineComponent({
    components: { CustomActivatorSelect, SmileSelector },
    props: {
        // текущий SMS-текст (контролируемый: значение приходит сверху, наружу летит 'input')
        value: { type: String, default: '' },
        // список переменных для вставки — { id, name }, id = алиас без фигурных скобок
        varsList: { type: Array as PropType<Array<{ id: string; name: string }>>, default: () => [] },
        // 'activation' — пусто = нейтральный пропуск SMS-шага; 'trigger' — пусто = требуем минимум 3
        validationMode: { type: String as PropType<'activation' | 'trigger'>, default: 'trigger' },
        showPreview: { type: Boolean, default: true },
        maxlength: { type: Number, default: 420 },
    },
    setup(props, { emit }) {
        const textarea = ref<HTMLTextAreaElement | null>(null)

        // длина в UTF-16 (эмодзи = 2), фикс-переменные по своей длине, остальные обнуляются
        // и помечают hasDynamic. ≤70 → 1 SMS, дальше по 67.
        const segmentInfo = computed(() => {
            const text = props.value || ''
            let hasDynamic = false
            const rendered = text.replace(/\{[^{}]+\}/g, (token: string) => {
                if (SMS_FIXED_LENGTHS[token] !== undefined) return '0'.repeat(SMS_FIXED_LENGTHS[token])
                hasDynamic = true
                return ''
            })
            const length = rendered.length
            let segments = 0
            if (text.trim()) segments = length <= 70 ? 1 : Math.ceil(length / 67)
            if (hasDynamic && segments === 0) segments = 1
            return { length, segments, hasDynamic }
        })

        const charCountText = computed(() => {
            const { length, hasDynamic } = segmentInfo.value
            return `${length}${hasDynamic ? '+' : ''} ${pluralRu(length, 'символ', 'символа', 'символов')}`
        })

        const segmentText = computed(() => {
            const { segments, hasDynamic } = segmentInfo.value
            return `${hasDynamic ? 'от ' : ''}${segments} SMS`
        })

        const validation = computed(() => {
            const raw = props.value || ''
            if (!raw.trim()) {
                if (props.validationMode === 'activation') {
                    return {
                        cls: 'neutral',
                        text: 'Текст не задан — при отправке будет пропуск SMS-шага каскада',
                    }
                }
                return { cls: 'warn', text: 'Введите минимум 3 символа' }
            }
            if (raw.trim().length < 3) return { cls: 'warn', text: 'Введите минимум 3 символа' }
            return { cls: 'ok', text: 'Текст готов' }
        })

        const insertAtCursor = (insertText: string) => {
            const el = textarea.value
            const cur = props.value || ''
            const start = el && el.selectionStart != null ? el.selectionStart : cur.length
            emit('input', cur.slice(0, start) + insertText + cur.slice(start))
            nextTick(() => {
                if (!el) return
                const pos = start + insertText.length
                el.focus()
                el.setSelectionRange(pos, pos)
            })
        }
        const insertVar = (varAlias: string) => insertAtCursor(` {${varAlias}}`)
        const insertEmoji = (emoji: string) => insertAtCursor(emoji)
        const clear = () => {
            emit('input', '')
            nextTick(() => textarea.value && textarea.value.focus())
        }

        return { textarea, segmentInfo, charCountText, segmentText, validation, insertVar, insertEmoji, clear }
    },
})
